import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import * as QRCode from 'qrcode-generator';

@Component({
  selector: 'app-template-video-focused',
  templateUrl: './template-video-focused.component.html',
  styleUrls: ['./template-video-focused.component.css'],
})
export class TemplateVideoFocusedComponent implements OnInit, OnChanges {
  // Same @Input() signature as Template01 for full compatibility with create-card-steps
  @Input() templateName: string = 'Video Spotlight Card';
  @Input() buttonColor: string;
  @Input() firstName: string;
  @Input() lastName: string;
  @Input() companyName: string;
  @Input() jobTitle: string;
  @Input() content: string;
  @Input() email: string;

  @Input() linkedin: string;
  @Input() youtube: string;
  @Input() twitter: string;
  @Input() facebook: string;
  @Input() instagram: string;
  @Input() pinterest: string;

  @Input() address: string;
  @Input() phone: string;
  @Input() phoneAllow: boolean;
  @Input() forwardCard: boolean;
  @Input() saveCard: boolean;
  @Input() inviteCode: boolean;
  @Input() referal: any;
  @Input() photo: File;
  @Input() buttons: Array<{ LinkButtonLink: string; LinkButtonTitle: string }>;
  @Input() umyotubeArray: any;
  @Input() vimeoArray: any;
  @Input() youtubeArray: any;

  // Video-Focused card ignores: gpa, age, weight, height, grade, school,
  // logo, productImages — these are collected in the form but not shown here.

  qrCodeImage: SafeResourceUrl | null = null;
  sanitizedUrlsCache: Map<string, SafeResourceUrl> = new Map<string, SafeResourceUrl>();
  imageSrc: string | ArrayBuffer | null = 'assets/images/john-doe-avatar.jpg';

  constructor(private sanitizer: DomSanitizer, private router: Router) {}

  ngOnInit(): void {
    this.generateQRCode();
  }

  generateQRCode() {
    const url = window.location.href;
    const qr = QRCode(0, 'L');
    qr.addData(url);
    qr.make();
    const qrImageTag = qr.createImgTag(5);
    const div = document.createElement('div');
    div.innerHTML = qrImageTag;
    const img = div.firstChild as HTMLImageElement;
    this.qrCodeImage = this.sanitizer.bypassSecurityTrustResourceUrl(img.src);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['photo']) {
      this.loadImage();
    }
    if (changes['youtubeArray'] || changes['umyotubeArray'] || changes['vimeoArray']) {
      this.sanitizedUrlsCache.clear();
    }
  }

  private loadImage(): void {
    if (this.photo instanceof File) {
      const reader = new FileReader();
      reader.onload = (event) => {
        this.imageSrc = event.target?.result;
      };
      reader.readAsDataURL(this.photo);
    } else if (typeof this.photo === 'string') {
      this.imageSrc = this.photo;
    }
  }

  // --- Video helpers (same logic as Template01, kept identical for consistency) ---

  convertToEmbeddedFormat(url: string): string {
    const videoId = this.extractVideoId(url);
    if (videoId === '') return '';
    return `https://www.youtube.com/embed/${videoId}`;
  }

  extractVideoId(url: string): string {
    const youtubeRegex = /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(youtubeRegex);
    return match && match[1] ? match[1] : '';
  }

  sanitizeYouTubeUrl(url: string): SafeResourceUrl {
    const formatedUrl = this.convertToEmbeddedFormat(url);
    if (this.sanitizedUrlsCache.has(formatedUrl)) {
      return this.sanitizedUrlsCache.get(formatedUrl)!;
    }
    const sanitizedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(formatedUrl);
    this.sanitizedUrlsCache.set(formatedUrl, sanitizedUrl);
    return sanitizedUrl;
  }

  convertDailymotionToEmbeddedFormat(url: string): string {
    const videoId = this.extractDailymotionVideoId(url);
    return videoId ? `https://www.dailymotion.com/embed/video/${videoId}` : '';
  }

  extractDailymotionVideoId(url: string): string {
    const match = url.match(/https:\/\/dai\.ly\/([a-zA-Z0-9]+)/);
    return match && match[1] ? match[1] : '';
  }

  sanitizeDailymotionUrl(url: string): SafeResourceUrl {
    const formattedUrl = this.convertDailymotionToEmbeddedFormat(url);
    if (formattedUrl === '') return '';
    if (this.sanitizedUrlsCache.has(formattedUrl)) {
      return this.sanitizedUrlsCache.get(formattedUrl)!;
    }
    const sanitizedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(formattedUrl);
    this.sanitizedUrlsCache.set(formattedUrl, sanitizedUrl);
    return sanitizedUrl;
  }

  sanitizeumyovideo(url: string): SafeResourceUrl {
    if (this.sanitizedUrlsCache.has(url)) {
      return this.sanitizedUrlsCache.get(url)!;
    }
    const sanitizedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.sanitizedUrlsCache.set(url, sanitizedUrl);
    return sanitizedUrl;
  }

  sanitizeVimeo(url: string): SafeResourceUrl {
    if (url === '') return '';
    if (this.sanitizedUrlsCache.has(url)) {
      return this.sanitizedUrlsCache.get(url)!;
    }
    const sanitizedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.sanitizedUrlsCache.set(url, sanitizedUrl);
    return sanitizedUrl;
  }

  navigateToHome(): void {
    localStorage.clear();
    const referralId = this.referal || '';
    this.router.navigate(['/'], { queryParams: { referralId } });
  }

  downloadVCard(): void {
    const vCardData = `BEGIN:VCARD
VERSION:3.0
FN:${this.firstName} ${this.lastName}
ORG:${this.companyName}
EMAIL:${this.email}
TEL:${this.phone}
ADR:${this.address}
END:VCARD`;
    const blob = new Blob([vCardData], { type: 'text/vcard' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contact.vcf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }
}